/**
 * Role conflict and advisory rules for the Add/Edit User dialog.
 *
 * Every rule here was derived from permission gates in the estate's own code,
 * not from intent or inference. Each carries the file:line that justifies it,
 * so a future reader can re-check the rule against the code it describes —
 * and delete the rule if that code changes.
 *
 * Three drafted rules were investigated and deliberately NOT implemented:
 *
 *   Operator <-> Operator View Only
 *     Not a conflict. The read/write split is real (GETs admit both, writes
 *     admit only dsm_operator) but gates use additive `any()`, so holding both
 *     is plain read-write and view-only is inert. It is misleading rather than
 *     broken, so it is an advisory below, not a block.
 *
 *   Municipality <-> other DSM roles
 *     Blocking this would BREAK the product. dsm_municipality has no scoping
 *     anywhere — no user field, no JWT claim, no filtered queryset — and the
 *     municipality pages call operator-gated endpoints
 *     (SmartNode demand_side_management/views.py:2548-2553, :2785-2788), so a
 *     municipality user needs dsm_operator for that section to work at all.
 *
 *   QE <-> Installer
 *     The deny-list at custom_user/views.py:614 looks like it excludes
 *     dual-role users from the QE roster, but `~Q` on a multi-valued relation
 *     inside one filter() does not do that — verified empirically, both users
 *     are returned. The deny-list is a silent no-op (its own bug); it is not
 *     grounds for forbidding the combination, which the backend's own tests
 *     create as a normal payload.
 */

const FIELD_ADMIN = "dsm_field_admin";
const IMS_ADMIN = "neura_ims_admin";
const SUPER_USER = "global_super_user";
const INSTALLER = "dsm_installer";
const OPERATOR = "dsm_operator";
const OPERATOR_VIEW_ONLY = "dsm_operator_view_only";

export const REGION_PREFIX = "ims_region_";

export const isRegion = (unique_name) => unique_name.startsWith(REGION_PREFIX);

/**
 * Pairs that cause real breakage, each with the code that proves it.
 * Order within a pair is irrelevant — conflicts are symmetric.
 */
export const ROLE_CONFLICTS = [
  {
    pair: [FIELD_ADMIN, IMS_ADMIN],
    reason:
      "IMS withdraws a Field Admin's access by clearing their team, but IMS Admins skip that check — deactivating their team would not remove their access.",
    evidence: "IMS ims/views.py:944, :1704",
  },
  {
    pair: [FIELD_ADMIN, SUPER_USER],
    reason:
      "IMS withdraws a Field Admin's access by clearing their team, but Super Users skip that check — deactivating their team would not remove their access.",
    evidence: "IMS ims/views.py:944, :1704",
  },
  {
    pair: [FIELD_ADMIN, INSTALLER],
    reason:
      "Nothing assigns a Field Admin an installer company, so job cards they create are saved with no company and disappear from the QE queue and company lists.",
    evidence: "Installer field_admin/views.py:392-402, installer/views.py:143-145",
  },
];

/**
 * The conflict blocking `unique_name`, given what is already selected — or null.
 *
 * Returns the first match only; one reason is enough to explain a disabled box.
 */
export function conflictFor(unique_name, selectedGroups) {
  for (const rule of ROLE_CONFLICTS) {
    const [a, b] = rule.pair;
    if (unique_name === a && selectedGroups.includes(b)) return { ...rule, conflictsWith: b };
    if (unique_name === b && selectedGroups.includes(a)) return { ...rule, conflictsWith: a };
  }
  return null;
}

/** Conflicting pairs already present in a selection — legacy data, or a rule added later. */
export function existingConflicts(selectedGroups) {
  return ROLE_CONFLICTS.filter(
    (rule) => selectedGroups.includes(rule.pair[0]) && selectedGroups.includes(rule.pair[1])
  );
}

/**
 * Non-blocking notes. These describe behaviour that is surprising but not
 * broken, so they inform rather than prevent.
 */
export function advisoriesFor(selectedGroups) {
  const notes = [];
  const regions = selectedGroups.filter(isRegion);

  if (selectedGroups.includes(OPERATOR) && selectedGroups.includes(OPERATOR_VIEW_ONLY)) {
    notes.push({
      id: "operator-redundant",
      level: "info",
      text: "Operator View Only has no effect alongside Operator — permissions are additive, so this user has full write access despite the label.",
    });
  }

  // region_helpers.py:10 returns every active region before region codes are
  // even read, so a region on a Super User is inert.
  if (selectedGroups.includes(SUPER_USER) && regions.length) {
    notes.push({
      id: "superuser-region-inert",
      level: "info",
      text: "Super Users already see every region, so the regions selected here will have no effect.",
    });
  }

  // region_helpers.py:19-24 — the one place in the system where adding a group
  // reduces access, so it is worth saying out loud.
  if (selectedGroups.includes(IMS_ADMIN) && !selectedGroups.includes(SUPER_USER) && regions.length) {
    notes.push({
      id: "imsadmin-region-narrows",
      level: "warning",
      text: "Adding a region restricts this IMS Admin to it. With no region selected they can see all regions.",
    });
  }

  return notes;
}
